/**
 * Has the USER marked this campaign complete?
 *
 * Mirrors email_automation/campaign_completion.py::campaign_is_complete, and
 * the mirroring is the point: completion is the only thing that stops SiteSift
 * reading a campaign, so the dashboard must decide it from the same field, the
 * same way, or the two drift.
 *
 * True only for a marker that names the person who made the decision. An
 * unsigned marker is what an inference would write, and the backend treats it
 * as inert; a dashboard that believed it would show "Completed" over a campaign
 * the product is still polling.
 *
 * `status === 'completed'` is deliberately NOT completion. That string was
 * written by the retired `_maybe_mark_client_completed` heuristic -- by the
 * product, never by her.
 */
export function campaignIsMarkedComplete(client) {
  const completion = client && client.completion;
  if (!completion || typeof completion !== 'object') return false;
  if (String(completion.state || '').trim().toLowerCase() !== 'complete') return false;
  return typeof completion.markedBy === 'string' && completion.markedBy.trim().length > 0;
}

function firstText(...values) {
  for (const value of values) {
    const normalized = String(value || '').trim();
    if (normalized) return normalized;
  }
  return '';
}

function normalizePropertyIdentity(property) {
  if (typeof property === 'string') {
    return property.trim();
  }

  if (!property || typeof property !== 'object') {
    return '';
  }

  const row = property.rowIndex ?? property.rowNumber ?? property.row;
  const address = firstText(
    property.address,
    property.propertyAddress,
    property.property_address,
    property.rowAnchor
  );
  const city = firstText(property.city, property.market);

  if (address) {
    return ['address', address.toLowerCase(), city.toLowerCase()].filter(Boolean).join('|');
  }

  const name = firstText(property.propertyName, property.name);
  if (name) {
    return ['name', name.toLowerCase(), city.toLowerCase(), row != null ? `row:${row}` : '']
      .filter(Boolean)
      .join('|');
  }

  return row != null ? `row:${row}` : '';
}

export function countCampaignProperties(client = {}) {
  const properties = new Set();

  Object.values(client.emailToProperties || {}).forEach(propertyList => {
    if (!Array.isArray(propertyList)) return;

    propertyList.forEach(property => {
      const normalized = normalizePropertyIdentity(property);
      if (normalized) {
        properties.add(normalized);
      }
    });
  });

  return properties.size;
}

export function resolveCampaignCompletion(client = {}, counts = {}) {
  const totalProperties = countCampaignProperties(client);
  const completedProperties = Number(counts.completedProperties ?? counts.completed ?? 0);
  const unavailableProperties = Number(counts.unavailableProperties ?? counts.unavailable ?? 0);
  const resolvedProperties = completedProperties + unavailableProperties;
  const hasAuthoritativeCompletion = String(client.status || client.campaignStatus || '')
    .trim()
    .toLowerCase() === 'completed';

  return {
    totalProperties,
    completedProperties,
    unavailableProperties,
    resolvedProperties,
    isCampaignComplete: campaignIsMarkedComplete(client)
      || hasAuthoritativeCompletion
      || (totalProperties > 0 && resolvedProperties >= totalProperties)
  };
}
